import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, CheckCircle2 } from 'lucide-react';
import Button from '../../../../components/common/Button';
import Card from '../../../../components/common/Card';
import Alert from '../../../../components/common/Alert';
import { adminAPI } from '../../../../services/adminAPI';

export default function SubscriptionSelector({ onComplete, initialData = {}, onBack }) {
  const [selectedPlan, setSelectedPlan] = useState(initialData.planId || '');
  const [billingPeriod, setBillingPeriod] = useState(initialData.billingPeriod || 'monthly');

  const { 
    data: plans = [], 
    isLoading,
    error
  } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await adminAPI.getPlans();
      return response.data?.plans || [];
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Find the selected plan object
    const plan = plans.find(p => p.id === selectedPlan);
    
    onComplete({
      planId: selectedPlan,
      planName: plan?.name || '',
      billingPeriod
    });
  };

  const calculatePrice = (plan) => {
    if (!plan) return 0;
    const basePrice = billingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price;
    const discount = billingPeriod === 'yearly' ? 0.2 : 0;
    return (basePrice * (1 - discount)) / 100;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error">
        Failed to load subscription plans. Please try again.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="inline-flex items-center rounded-full bg-gray-100 p-0.5 mb-6">
        <button
          onClick={() => setBillingPeriod('monthly')}
          className={`relative flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 ease-in-out rounded-full ${
            billingPeriod === 'monthly'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingPeriod('yearly')}
          className={`relative flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 ease-in-out rounded-full ${
            billingPeriod === 'yearly'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span>Yearly</span>
          <span className="ml-1.5 flex items-center text-xs font-normal text-green-600 bg-green-100 rounded-full px-2 py-0.5">
            -20%
          </span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div 
            key={plan.id} 
            onClick={() => setSelectedPlan(plan.id)}
            className={`p-6 cursor-pointer transition-all rounded-xl ${
              selectedPlan === plan.id 
                ? 'ring-2 ring-primary ring-offset-2 shadow-md' 
                : 'shadow-sm hover:shadow-md border border-gray-100'
            }`}
          >
            {selectedPlan === plan.id && (
              <div className="absolute top-4 right-4">
                <CheckCircle2 className="h-6 w-6 text-primary" />
              </div>
            )}
            
            <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
            <div className="mb-6">
              <p className="text-4xl font-bold text-gray-900">
                ${calculatePrice(plan).toFixed(2)}
                <span className="text-base font-normal text-gray-500 ml-1">
                  /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                </span>
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {plan.metadata?.feature_highlights?.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={!selectedPlan}
        >
          Continue
        </Button>
      </div>
    </div>
  );
} 